import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { observer, useObserver } from 'mobx-react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';

import useCart from 'shared/hooks/use-cart';
import useErnie from 'shared/hooks/use-ernie';
import useStores from 'shared/hooks/use-stores';
import useTranslation from 'src/hooks/use-translation';

import { CheckboxOld } from 'shared/components';
import getCustomerSubscriptions from 'shared/graphql/subscription/queries/get-customer-subscriptions.gql';
import { CheckoutLabelGrouping, CheckoutLabel, CheckoutBody } from 'checkout/components/styles/common';
import { useUpdateSubscription } from 'src/state/actions/users';

const legacyErnieMsg = `Please save your info for next time to begin receiving emails from this dispensary.`;
// eslint-disable-next-line max-len
const ernieMsg = `Please save your info for next time to begin receiving emails and text messages from this dispensary.`;

function useSubscription({ isLegacy }) {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hideSection, setHideSection] = useState(false);
  const [subscribed, setSubscribed] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const Cart = useCart();
  const showErnie = useErnie();
  const { apolloClient, User } = useStores();
  const updateSubscription = useUpdateSubscription();
  const loggedIn = useObserver(() => User.exists);

  // eslint-disable-next-line consistent-return
  const handleOnClick = (event) => {
    // if the user already has an account, update the email subscription.
    if (Cart.order.isGuestOrder) {
      // if the user hasn't signed up for the account -- throw error to alert them to sign up.
      if (Cart.guestInfoToSave && !Cart.order.guestCustomer?.email) {
        event.preventDefault();
        showErnie(isLegacy ? legacyErnieMsg : ernieMsg, `error`);
        return false;
      }
      Cart.order.guestCustomer.emailOptIn = event.target.checked;
    } else if (subscriptionId) {
      updateSubscription(subscriptionId, event.target.checked);
    }
    setSubscribed(event.target.checked);
  };

  const { loading, data } = useQuery(getCustomerSubscriptions, {
    client: apolloClient,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !loggedIn,
  });

  useEffect(() => {
    const subscriptions = data?.getCustomerSubscriptions;
    const subscription = _.find(subscriptions, { dispensaryId: Cart.order.dispensary?.id });

    if (!loading && subscription) {
      setCurrentSubscription(subscription);
      setSubscribed(subscription?.emailOptIn ?? false);
      setSubscriptionId(subscription.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading]);

  useEffect(() => {
    if (!_.isEmpty(currentSubscription)) {
      setSubscribed(false);
    }
    if (!loggedIn) {
      setHideSection(false);
      return;
    }
    if (loggedIn && currentSubscription) {
      setHideSection(true);
      return;
    }
    setHideSection(false);
  }, [currentSubscription, loggedIn]);

  return { Cart, subscribed, handleOnClick, loading, hideSection };
}

function Subscription() {
  const { Cart, subscribed, handleOnClick, loading, hideSection } = useSubscription({ isLegacy: false });
  const { t } = useTranslation();

  if (loading || hideSection) {
    return null;
  }

  const labelText = t(
    'opt-in-promo-emails.label',
    'Subscribe to marketing emails and text messages from this dispensary'
  );

  return (
    <CheckoutLabelGrouping id='subscriptions'>
      <CheckoutLabel />
      <CheckoutBody>
        <StyledCheckbox
          name='subscribeToPromoEmail'
          label={labelText}
          aria-label={labelText}
          data-cy='opt-in-promo-emails'
          data-test='opt-in-promo-emails'
          checked={Cart.order.isGuestOrder ? Cart.order.guestCustomer?.emailOptIn : subscribed}
          onClick={(event) => handleOnClick(event)}
          color='#6b6f73'
        />
      </CheckoutBody>
    </CheckoutLabelGrouping>
  );
}

const StyledCheckbox = styled(CheckboxOld)`
  max-width: 390px;
`;

const subscription = observer(Subscription);
export { subscription as Subscription };
